import { useReducer, useEffect, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Select,
  Modal
} from "@theorgchartdev/storybook";
import { SelectableSearchMenu } from "../../Menus/BasicChartSearchMenu/SelectableSearchMenu";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { getCurrentChartName } from "@/Utils/Misc";
import { useRouteState } from "@/hooks/useRouteState";
import { useTopic } from "@/hooks/useTopic";
import { ListItemOption } from "@theorgchartdev/storybook/dist/components/List/ListItem";
import LevelHandler from "@/Utils/Chart/LevelHandler";
import { BoxChip } from "@/Utils/BoxChip";
import { BuilderModalProps, getTopOfChartFunc } from "@/Utils/Chart/NewDocForm";
import {
  customChartFormReducer,
  CustomChartFormState,
  getInitialState
} from "@/Utils/CustomChart/NewCustomChartForm";
import { CustomChartRuleInput } from "./CustomChartRuleInput";
import {
  computeErrors,
  intersectErrors,
  UIRuleError
} from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";
import { useToast } from "@/providers/ToastProvider";

const CustomChartBuilderModal = (props: BuilderModalProps<CustomChartFormState>) => {
  const { show, onClose, onCreate } = props;
  const currentChartName = getCurrentChartName();
  const currentDocID = StateLib.GetDoc()?.GetChartID();
  const [baseChartID, setBaseChartID] = useState(currentDocID);
  const { topOfChart: topOfChartParam } = useRouteState();

  // Form state
  const [formState, dispatch] = useReducer(customChartFormReducer, getInitialState());
  const [submittedErrors, setSubmittedErrors] = useState<UIRuleError | null>(null);
  const { addToast } = useToast() as any;

  const getTopOfChart = getTopOfChartFunc(dispatch);

  // URL-driven: `?topOfChart=` wins over the scm's current top so deep links
  // like /base/1?create=true&topOfChart=9 pre-select record 9 in the builder.
  useEffect(() => {
    if (show) {
      getTopOfChart(topOfChartParam);
    }
  }, [show, topOfChartParam]);

  useEffect(() => {
    if (currentDocID !== null) {
      if (currentDocID !== baseChartID) {
        dispatch({ type: "RESET", payload: null });
      }
      setBaseChartID(currentDocID);
    }
  }, [currentDocID]);

  // Cold-start race: when `?create=true` survives a reload, the modal can
  // mount before chart data arrives. `getTopOfChart` short-circuits when the
  // subchart manager has no current subchart, so the first call populates
  // nothing. Re-run it on `chartDocumentLoaded` as a fallback whenever the
  // modal is still visible and the top box is still empty.
  useTopic("chartDocumentLoaded", () => {
    if (show && formState.topbox == null) {
      getTopOfChart(topOfChartParam);
    }
  });

  const handleClose = () => {
    setSubmittedErrors(null);
    onClose();
  };

  const handleCreate = () => {
    if (formState.filter) {
      const errors = computeErrors(formState.filter);
      if (errors.hasError) {
        setSubmittedErrors(errors);
        return;
      }
    }
    const builderInfo = GetBuilderInfo();
    dispatch({ type: "RESET", payload: null });
    addToast({
      message: appstr("custom chart created", { name: currentChartName }),
      status: "success"
    });
    onCreate(builderInfo);
  };

  const handleRuleChange = (value: any) => {
    dispatch({ type: "SET_FILTER", payload: value });
    setSubmittedErrors(prev => {
      if (!prev) return null;
      if (!value || formState.filter?.GetIsComplex() !== value?.GetIsComplex()) return null;
      const prevPairCount = formState.filter?.GetRulePairList()?.length ?? 0;
      const nextPairCount = value?.GetRulePairList()?.length ?? 0;
      if (prevPairCount !== nextPairCount) return null;
      return intersectErrors(prev, computeErrors(value));
    });
  };

  const GetBuilderInfo = () => {
    let builderInfo: any = {};
    let doc = StateLib.GetDoc();
    let activeViewID = doc?.GetActiveTemplate().GetTemplateID();
    builderInfo["liveChart"] = false;
    builderInfo["levels"] = formState.levels;
    builderInfo["topBoxID"] = formState.topbox?.topBoxID;
    builderInfo["importFilter"] = {
      rule: formState.filter
    };
    builderInfo["viewid"] = activeViewID;

    return builderInfo;
  };

  const isPosition = StateLib.GetDoc()?.IsPositionDocument();
  const baseClass = "ocn-builder";
  const levelOptions = LevelHandler.buildLevelOptions((level: ListItemOption) =>
    dispatch({ type: "SET_LEVELS", payload: level.value })
  );

  const requiredTicker = <span className={"ocn-form-label-required"}> *</span>;

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="builder-card"
        show={show}
        centerScreen={true}
        onClose={handleClose}
        fullWidth={true}
        customStyle={{ padding: 0, width: "600px" }}
        className="ocn-custom-chart-builder-modal ocn-chart-doc-builder-modal"
      >
        <DialogTitle divider={true} onClose={handleClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"planning"} />
            {appstrp("Create custom chart from", `: ${currentChartName}`)}
          </span>
        </DialogTitle>

        {/* CC TOPBOX */}
        <DialogContent dividers={false}>
          <div className="ocn-builder">
            <label className="ocn-form-label ocn-form-input-label-vertical-gap">
              {appstr(isPosition ? "Build Position Doc" : "Build Employees Chart", {
                document: appstr("Custom Chart").toLowerCase()
              })}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <SelectableSearchMenu
                dataTestId="custom-chart-topbox-search"
                inputSize="medium"
                inputProps={{
                  "aria-label": "Which employee would you like at the top of this chart?"
                }}
                listWidth="fill"
                displayRecents={false}
                selectedItem={formState.topbox}
                onRemoveItem={() => dispatch({ type: "SET_TOPBOX", payload: null })}
                searchMenuProps={{
                  clearOnSelect: true,
                  clearable: true,
                  placeholder: formState.topbox ? "" : appstr("Search by name or title")
                }}
                handleSearchOptionClick={(target: any) => {
                  const lookupRecordObj = BoxChip.FromSearchResult(
                    target.result,
                    false,
                    StateLib.GetDoc()
                  );
                  dispatch({
                    type: "SET_TOPBOX",
                    payload: lookupRecordObj,
                    isPosition
                  });
                }}
                queryOptions={{ includeAux: false }}
              />
            </div>
          </div>
        </DialogContent>

        {/* CC LEVELS */}
        <DialogContent dividers={false}>
          <span className={baseClass}>
            <label className="ocn-form-label ocn-form-input-label-vertical-gap">
              {appstr(isPosition ? "Select Build Position Level" : "Select Build Chart Level")}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <span className={"ocn-builder-edit-text"}>
                <Select
                  dataTestId={"builder-lvls-menu"}
                  optionSize="medium"
                  selectSize="medium"
                  value={formState.levels}
                  options={levelOptions}
                  inputProps={{
                    "aria-label": "How many levels under this employee would you like to include?"
                  }}
                />
              </span>
            </div>
          </span>
        </DialogContent>

        {/* CC FILTER */}
        <DialogContent dividers={true}>
          <span className={baseClass}>
            <label className="ocn-form-label">{appstr("Chart Builder Filter")}</label>
            <div className="ocn-section-prompt-text-description ocn-form-input-label-vertical-gap">
              {appstr("Chart Builder Filter Directions")}
            </div>
            <div className={"ocn-builder-edit"}>
              <span id={"builder-filter-btn"} className={"ocn-builder-edit-text"}>
                <CustomChartRuleInput
                  errors={submittedErrors}
                  rule={formState.filter}
                  setRule={handleRuleChange}
                />
              </span>
            </div>
          </span>
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions>
          <DialogActions.Tertiary onClick={handleClose} size="medium">
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleCreate}
            dataTestId={"builder-create-custom-chart-btn"}
            size="medium"
            disabled={!formState.isDirty}
          >
            {appstr("Create")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default CustomChartBuilderModal;
